import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../../../context/AuthContext";
import { Helmet } from "react-helmet-async";
import "./Checkout.css";

const CURRENCY = "€";

function Checkout() {
  const navigate = useNavigate();
  const { setShowAuth, user } = useContext(AuthContext);
  const [cartItems, setCartItems] = useState([]);
  const [showMore, setShowMore] = useState(false);

  const [agreedDisclaimer, setAgreedDisclaimer] = useState(false);
  const [agreedSmsTerms, setAgreedSmsTerms] = useState(false);
  const [agreedNotifications, setAgreedNotifications] = useState(false);

  const [formData, setFormData] = useState({
    email: "", phone: "", firstName: "", lastName: "",
    address: "", apartment: "", city: "", state: "",
    zip: "", country: "", reminder: "", hearAbout: "",
    contactMethod: "email", billingSame: true,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const savedCart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartItems(savedCart);
    if (user) {
      const fullName = user.name?.split(" ") || [];
      setFormData(prev => ({
        ...prev,
        email: user.email || "",
        firstName: fullName[0] || "",
        lastName: fullName.slice(1).join(" ") || ""
      }));
    }
  }, [user]);

  const total      = cartItems.reduce((sum, item) => sum + item.price * item.qty, 0);
  const shipping   = 25;
  const grandTotal = total + shipping;

  // Only the two outer checkboxes (disclaimer + notifications) are
  // required. The "Read more" inner checkbox is optional and does not
  // affect button state.
  const isFormValid =
    formData.email.trim() !== "" &&
    formData.firstName.trim() !== "" &&
    formData.lastName.trim() !== "" &&
    formData.city.trim() !== "" &&
    formData.country.trim() !== "" &&
    agreedDisclaimer &&
    agreedNotifications;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const storedUser   = JSON.parse(localStorage.getItem("user"));
    const shippingData = { ...formData, userId: storedUser?.id || null };
    localStorage.setItem("shippingData", JSON.stringify(shippingData));
    navigate("/checkout/medical");
  };

  return (
    <>
      <Helmet>
        <title>Checkout | Shipping Details</title>
        <meta name="description" content="Enter shipping details and complete your order securely." />
        <link rel="canonical" href="https://kamagrastar.eu/checkout" />
      </Helmet>

      <div className="checkout-page">

        {/* STEPS */}
        <div className="checkout-steps">
          <div className="step active"><span>1</span><p>Shipping</p></div>
          <div className="line"></div>
          <div className="step"><span>2</span><p>Medical Conditions</p></div>
          <div className="line"></div>
          <div className="step"><span>3</span><p>Payments</p></div>
        </div>

        <div className="checkout-wrapper">

          {/* LEFT */}
          <div className="checkout-left">
            <Link to="/cart" className="back-cart">&lt; Back to cart</Link>

            <div className="checkout-head">
              <h2>Shipping Address</h2>
              <p>Already have an account?
                <span className="signin-link" onClick={() => setShowAuth(true)}>Sign in</span>
              </p>
            </div>

            <form onSubmit={handleSubmit}>

              <div className="grid-2">
                <div>
                  <input type="email" name="email" placeholder="Your Email Address *"
                    value={formData.email} onChange={handleChange} required />
                  <small>We'll send your order confirmation here.</small>
                </div>
                <div>
                  <input type="text" name="phone" placeholder="+49 81234 56789"
                    value={formData.phone} onChange={handleChange} />
                  <small>Do not include country code.</small>
                </div>
              </div>

              <div className="grid-2">
                <input type="text" name="firstName" placeholder="First name *"
                  value={formData.firstName} onChange={handleChange} required />
                <input type="text" name="lastName" placeholder="Last name *"
                  value={formData.lastName} onChange={handleChange} required />
              </div>

              <input type="text" name="address" placeholder="Address"
                value={formData.address} onChange={handleChange} />
              <input type="text" name="apartment" placeholder="Add Apartment, Suite, etc."
                value={formData.apartment} onChange={handleChange} />

              <div className="grid-2">
                <input type="text" name="city" placeholder="City *"
                  value={formData.city} onChange={handleChange} required />
                <input type="text" name="state" placeholder="State *"
                  value={formData.state} onChange={handleChange} />
              </div>

              <div className="grid-2">
                <input type="text" name="zip" placeholder="Zip/Postal Code *"
                  value={formData.zip} onChange={handleChange} />
                <select name="country" value={formData.country} onChange={handleChange} required>
                  <option value="">Select Country</option>
                  <option>Austria</option><option>Belgium</option><option>Bulgaria</option>
                  <option>Croatia</option><option>Cyprus</option><option>Czech Republic</option>
                  <option>Denmark</option><option>Estonia</option><option>Finland</option>
                  <option>France</option><option>Germany</option><option>Greece</option>
                  <option>Hungary</option><option>Ireland</option><option>Italy</option>
                  <option>Latvia</option><option>Lithuania</option><option>Luxembourg</option>
                  <option>Malta</option><option>Netherlands</option><option>Norway</option>
                  <option>Poland</option><option>Portugal</option><option>Romania</option>
                  <option>Slovakia</option><option>Slovenia</option><option>Spain</option>
                  <option>Sweden</option><option>Switzerland</option><option>United Kingdom</option>
                </select>
              </div>

              <div className="grid-2">
                <select name="reminder" value={formData.reminder} onChange={handleChange}>
                  <option value="">Set reminder for next order</option>
                  <option>15 days</option><option>30 days</option>
                  <option>45 days</option><option>60 days</option>
                </select>
                <select name="hearAbout" value={formData.hearAbout} onChange={handleChange}>
                  <option value="">Heard About Us</option>
                  <option>Google</option><option>Instagram</option><option>Facebook</option>
                </select>
              </div>

              <div className="contact-box">
                <h4>How to reach you?</h4>
                <p>Select a channel to get order updates</p>
                <div className="radio-group">
                  {["email", "sms", "phone"].map(m => (
                    <label key={m}>
                      <input type="radio" name="contactMethod" value={m}
                        checked={formData.contactMethod === m} onChange={handleChange} />
                      {m.charAt(0).toUpperCase() + m.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div className="checkout-disclaimer">

              <label className="policy-line">
                <input
                  type="checkbox"
                  required
                  checked={agreedDisclaimer}
                  onChange={(e) => setAgreedDisclaimer(e.target.checked)}
                />
                <div>
                  <strong>I agree to your disclaimer policy:</strong>
                  <p>
                    Kamagra Star complies with all applicable local legal regulations
                    and employs qualified pharmacists in certified facilities.
                    By creating an account or placing an order, users expressly consent
                    to receive promotional communications VIA TEXT MESSAGE; all personal
                    information is securely protected and will not be disclosed to third
                    parties. You can always opt out of text messages by replying STOP.
                  </p>

                  {!showMore ? (
                    <span className="read-toggle" onClick={() => setShowMore(true)}>Read more</span>
                  ) : (
                    <>
                      <div className="message-wrapper">
                        <label className="policy-check-full">
                          <input
                            type="checkbox"
                            checked={agreedSmsTerms}
                            onChange={(e) => setAgreedSmsTerms(e.target.checked)}
                          />
                          <div className="message-box">
                            <strong>Agreement to Receive Text and Whatsapp Messages</strong>
                            <p>
                              By providing your mobile number, you agree that
                              <a href="/"> KamagraStar.com </a>
                              may send you periodic SMS or MMS messages containing
                              but not limited to important information, updates, deals, and specials.
                            </p>
                            <ul>
                              <li>You will receive up to 4 messages per month.</li>
                              <li>You may unsubscribe at any time by texting the word STOP to the same message. You may receive a subsequent message confirming your opt-out request.</li>
                              <li>For help, send the word HELP to the same message.</li>
                              <li>Message and data rates may apply.</li>
                              <li>United States Participating Carriers Include AT&T, T-Mobile®, Verizon Wireless, Sprint, Boost, U.S. Cellular®, MetroPCS®, InterOp, Cellcom, C Spire Wireless, Cricket, Virgin Mobile and others.</li>
                              <li>T-Mobile is not liable for delayed or undelivered messages.</li>
                              <li>You agree to notify us of any changes to your mobile number and update your account with us to reflect this change.</li>
                              <li>Data obtained from you in connection with this SMS service may include your cell phone number, your carrier's name, and the date, time and content of your messages, as well as other information that you provide. We may use this information to contact you and to provide the services you request from us.</li>
                              <li>By subscribing or otherwise using the service, you acknowledge and agree that we will have the right to change and/or terminate the service at any time, with or without cause and/or advance notice.</li>
                              <li>If you have any questions please drop us an email on <a href="mailto:support@kamagrarstar.com">support@kamagrarstar.com</a>.</li>
                              <li>Read the full <a href="/safe-shopping">Communication Policy here.</a></li>
                            </ul>
                            <h4>Will I be charged for the text messages I receive?</h4>
                            <p>
                              Though KamagraStar.com will never charge you for the text messages
                              you receive, depending on your phone plan, you may see some charges
                              from your mobile provider. Please reach out to your wireless provider
                              if you have questions about your text or data plan.
                            </p>
                            <p>
                              We try to deliver your order within 10 days, but in certain cases,
                              delivery may take up to twenty to thirty working days.
                            </p>
                          </div>
                        </label>
                      </div>
                      <span className="read-toggle" onClick={() => setShowMore(false)}>Read less</span>
                    </>
                  )}
                </div>
              </label>

              <label className="policy-line small-policy">
                <input
                  type="checkbox"
                  required
                  checked={agreedNotifications}
                  onChange={(e) => setAgreedNotifications(e.target.checked)}
                />
                <span>Receive Text and Email Notification
                  <a href="/safe-shopping"> Read Communication Policy Here</a>
                </span>
              </label>

              <button
                type="submit"
                className="continue-btn"
                disabled={!isFormValid}
              >
                Save and Continue
              </button>

            </div>

            </form>
          </div>

          {/* RIGHT */}
          <div className="checkout-right">
            <div className="summary-card">
              <h3>Order Summary ({cartItems.length} items)</h3>
              {cartItems.map((item, index) => (
                <div className="summary-item" key={index}>
                  <img src={item.image} alt={item.productName} loading="lazy" />
                  <div className="summary-info">
                    <h4>{item.productName}</h4>
                    <p>{item.qty} x {item.packSize}</p>
                  </div>
                  <span>{CURRENCY}{(item.price * item.qty).toFixed(2)}</span>
                </div>
              ))}
              <div className="summary-total">
                <div><span>Subtotal</span><span>{CURRENCY}{total.toFixed(2)}</span></div>
                <div><span>International Shipping</span><span>{CURRENCY}{shipping.toFixed(2)}</span></div>
                <div className="grand-total">
                  <span>Order Total</span>
                  <span>{CURRENCY}{grandTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}

export default Checkout;